// MA-874 (operator D-1101-23, D-1101-25) — the score-per-skill projection, as a pure leaf.
//
// masteryScore and computeSkillMasteryProjection moved here VERBATIM from
// masteryProjection.ts (MA-623's ONE mastery reader), which re-exports both
// unchanged, so every importer keeps working. The move lets a server bundle
// (the weekly recap's api/_lib/recapDetail.bundle.mjs, through overviewModel)
// use the projection without pulling masteryProjection's readTreeSecuredCount
// (the browser supabase client, Sentry, the local attempt store).
//
// This module is now the ONLY place the projection literal set is written; the
// §E3 single-formula guard (recapTreeSecuredParity.test.mjs) pins it HERE and
// pins masteryProjection.ts as a re-export. Keep it import-light: the adaptation
// evidence functions and types only.

import type { ChildAttempt } from "@/adaptation/types";
import { computeSkillMasteryState } from "@/adaptation/masteryEvidence";
import { evaluateTransferEvidence } from "@/adaptation/transferGating";
import { evaluateDelayedReview } from "@/adaptation/spacedReview";

/** The projection literal set (1.0/0.9/0.7/0.4): maps the engine's richer
 *  mastery state to the single display score `masteryTier` bands. This is a
 *  display projection of the engine's state, not a separate mastery
 *  formula — moved here verbatim from useSkillGraph.ts:43-49, which was its
 *  only home before MA-623 (the private-function root of the recap/tree
 *  divergence). */
export function masteryScore(state: ReturnType<typeof computeSkillMasteryState>): number {
  if (state.durableMastery) return 1.0;
  if (state.trueMastery) return 0.9;
  if (state.provisionalMastery) return 0.7;
  if (state.readinessOnly) return 0.4;
  return Math.min(0.6, state.accuracyRate); // cap below provisional
}

/** Full per-skill projection: the mastery state PLUS the transfer/delayed-
 *  review evidence objects a caller may need for other display fields
 *  (useSkillGraph's MasteryRecord), computed exactly once so no consumer
 *  re-derives `computeSkillMasteryState`'s inputs independently. */
export function computeSkillMasteryProjection(
  skillId: string,
  attempts: readonly ChildAttempt[],
): {
  state: ReturnType<typeof computeSkillMasteryState>;
  transferEvidence: ReturnType<typeof evaluateTransferEvidence>;
  delayedReviewEvidence: ReturnType<typeof evaluateDelayedReview>;
  score: number;
} {
  const attemptsArr = attempts as ChildAttempt[];
  const transferAttempts = attemptsArr.filter(
    (a) => a.isTransferTask || a.transferTaskId !== null,
  );
  const transferEvidence = evaluateTransferEvidence(skillId, transferAttempts);
  const delayedReviewEvidence = evaluateDelayedReview(skillId, attemptsArr);
  const state = computeSkillMasteryState(
    skillId,
    attemptsArr,
    transferEvidence,
    delayedReviewEvidence,
  );
  return { state, transferEvidence, delayedReviewEvidence, score: masteryScore(state) };
}
